import { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import {
  CheckCircle,
  Clock,
  AlertCircle,
  PlayCircle,
  Circle,
  AlertTriangle,
  Wrench,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface NodeData {
  label: string;
  type?: string;
  status?: 'pending' | 'running' | 'completed' | 'failed';
  description?: string;
  // Support status for visual distinction (FR-8.3)
  supportStatus?: 'supported' | 'unsupported' | 'development-requested' | 'mocked';
  mockConfig?: {
    enabled: boolean;
    responseType?: 'success' | 'failure' | 'custom';
  };
}

const statusConfig = {
  pending: {
    icon: Clock,
    color: 'text-yellow-500',
    bg: 'bg-yellow-50 border-yellow-200',
  },
  running: {
    icon: PlayCircle,
    color: 'text-blue-500',
    bg: 'bg-blue-50 border-blue-200',
  },
  completed: {
    icon: CheckCircle,
    color: 'text-green-500',
    bg: 'bg-green-50 border-green-200',
  },
  failed: {
    icon: AlertCircle,
    color: 'text-red-500',
    bg: 'bg-red-50 border-red-200',
  },
};

const typeConfig = {
  start: {
    shape: 'rounded-full',
    color: 'bg-green-100 border-green-300',
  },
  end: {
    shape: 'rounded-full',
    color: 'bg-red-100 border-red-300',
  },
  task: {
    shape: 'rounded-lg',
    color: 'bg-white border-gray-200',
  },
  decision: {
    shape: 'rounded-lg rotate-45',
    color: 'bg-yellow-100 border-yellow-300',
  },
  process: {
    shape: 'rounded-lg',
    color: 'bg-blue-100 border-blue-300',
  },
};

// FR-8.3: Visual distinction for supported/unsupported features
const supportStatusConfig = {
  supported: {
    border: 'ring-2 ring-green-500 ring-offset-1',
    icon: CheckCircle,
    iconColor: 'text-green-500',
    label: 'Supported',
    description: 'This feature is fully supported and will execute as expected.',
  },
  unsupported: {
    border: 'ring-2 ring-orange-500 ring-offset-1',
    icon: AlertTriangle,
    iconColor: 'text-orange-500',
    label: 'Unsupported',
    description: 'This feature is not yet supported. Mocking is available for testing.',
  },
  'development-requested': {
    border: 'ring-2 ring-blue-500 ring-offset-1',
    icon: Wrench,
    iconColor: 'text-blue-500',
    label: 'In Development',
    description: 'Development has been requested for this feature.',
  },
  mocked: {
    border: 'ring-2 ring-purple-500 ring-offset-1 ring-dashed',
    icon: AlertTriangle,
    iconColor: 'text-purple-500',
    label: 'Mocked',
    description: 'This node is using mock responses for testing.',
  },
};

export const WorkflowNode = memo(function WorkflowNode({
  data,
  selected,
}: NodeProps) {
  const nodeData = data as unknown as NodeData;
  const status = nodeData.status || 'pending';
  const type = nodeData.type || 'task';
  const supportStatus = nodeData.supportStatus;
  const StatusIcon = statusConfig[status]?.icon || Circle;
  const typeStyle = typeConfig[type as keyof typeof typeConfig] || typeConfig.task;

  const isDecision = type === 'decision';
  const isStartOrEnd = type === 'start' || type === 'end';
  const isMocked = nodeData.mockConfig?.enabled;

  // Determine the actual support status to display
  const displaySupportStatus = isMocked ? 'mocked' : supportStatus;
  const displaySupportStyle = displaySupportStatus
    ? supportStatusConfig[displaySupportStatus]
    : null;

  const nodeContent = (
    <div
      className={cn(
        'min-w-[120px] border-2 px-4 py-3 shadow-sm transition-all relative',
        typeStyle.shape,
        typeStyle.color,
        selected && 'ring-2 ring-primary ring-offset-2',
        isDecision && 'flex items-center justify-center',
        // Apply support status styling
        !selected && displaySupportStyle?.border
      )}
    >
      {/* Support Status Indicator Badge */}
      {displaySupportStyle && !isStartOrEnd && (
        <div className="absolute -top-2 -right-2">
          <Badge
            variant="outline"
            className={cn(
              'h-5 px-1.5 text-[10px] font-medium',
              displaySupportStatus === 'supported' &&
                'bg-green-50 text-green-700 border-green-300',
              displaySupportStatus === 'unsupported' &&
                'bg-orange-50 text-orange-700 border-orange-300',
              displaySupportStatus === 'development-requested' &&
                'bg-blue-50 text-blue-700 border-blue-300',
              displaySupportStatus === 'mocked' &&
                'bg-purple-50 text-purple-700 border-purple-300'
            )}
          >
            {displaySupportStatus === 'supported' && (
              <CheckCircle className="h-3 w-3" />
            )}
            {displaySupportStatus === 'unsupported' && (
              <AlertTriangle className="h-3 w-3" />
            )}
            {displaySupportStatus === 'development-requested' && (
              <Wrench className="h-3 w-3" />
            )}
            {displaySupportStatus === 'mocked' && (
              <AlertTriangle className="h-3 w-3" />
            )}
          </Badge>
        </div>
      )}

      <div className={cn(isDecision && '-rotate-45')}>
        <div className="flex items-center gap-2">
          {!isStartOrEnd && (
            <StatusIcon
              className={cn('h-4 w-4', statusConfig[status]?.color)}
            />
          )}
          <span className="text-sm font-medium">{nodeData.label}</span>
        </div>
        {nodeData.description && !isDecision && (
          <p className="mt-1 text-xs text-muted-foreground">
            {nodeData.description}
          </p>
        )}
        {/* Mock indicator */}
        {isMocked && !isDecision && (
          <p className="mt-1 text-xs text-purple-600 font-medium">
            Mocked Response
          </p>
        )}
      </div>
    </div>
  );

  return (
    <>
      {type !== 'start' && (
        <Handle
          type="target"
          position={Position.Top}
          className="!h-3 !w-3 !border-2 !border-primary !bg-background"
        />
      )}

      {displaySupportStyle ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{nodeContent}</TooltipTrigger>
            <TooltipContent side="right" className="max-w-[200px]">
              <div className="flex items-start gap-2">
                <displaySupportStyle.icon
                  className={cn('h-4 w-4 mt-0.5', displaySupportStyle.iconColor)}
                />
                <div>
                  <p className="font-medium">{displaySupportStyle.label}</p>
                  <p className="text-xs text-muted-foreground">
                    {displaySupportStyle.description}
                  </p>
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        nodeContent
      )}

      {type !== 'end' && (
        <Handle
          type="source"
          position={Position.Bottom}
          className="!h-3 !w-3 !border-2 !border-primary !bg-background"
        />
      )}

      {isDecision && (
        <>
          <Handle
            type="source"
            position={Position.Left}
            id="no"
            className="!h-3 !w-3 !border-2 !border-red-500 !bg-background"
          />
          <Handle
            type="source"
            position={Position.Right}
            id="yes"
            className="!h-3 !w-3 !border-2 !border-green-500 !bg-background"
          />
        </>
      )}
    </>
  );
});
